import React, { useRef } from 'react';
import { connect, useDispatch } from 'react-redux';
import { Badge, ValidationMessage, withRules } from 'ctm-components';
import classNames from 'classnames';
import { Tooltip } from '@comparethemarketau/manor-tooltip';
import { useStyles } from './claim_detail_styles';
import { badgeButtons, timeframeButtons } from './claim_detail_variables';

class ClaimDetailStatus {

    constructor(claimDetail, touched) {
        this.dormantData = claimDetail;
        this.touched = touched;
    }

    setTouched(touched) {
        this.touched = touched;
    }

    updateDormant(claimDetail) {
        this.dormantData = claimDetail;
    }

}

const _ClaimDetail = ({ anyPreviousClaimsCount, currentClaimDetails, touched }) => {
    const dispatch = useDispatch();
    const classes = useStyles();
    const claimDetailsStatus = useRef(new Map());

    const setClaimDetailStatus = (claimNumber, { isTouched, dormant }) => {
        if (claimDetailsStatus.current.has(claimNumber)) {
            claimDetailsStatus.current.get(claimNumber).setTouched(isTouched);
            if (dormant != null) claimDetailsStatus.current.get(claimNumber).updateDormant(dormant);
        } else {
            claimDetailsStatus.current.set(claimNumber, new ClaimDetailStatus(dormant, isTouched));
        }
    };
    const stageChange = (stagedClaims) => {
        dispatch({
            type: 'SET_FORM_VALUE',
            value: stagedClaims,
            stateKey: 'vertical.quote',
            identifier: 'coverDetail.driver.claimsDetails'
        });
    };
    const listChange = (stagedClaims) => {
        dispatch({
            type: 'SET_CLAIM_DETAILS_FORM_VALUE_WITH_RESET',
            value: stagedClaims,
            stateKey: 'vertical.quote',
            identifier: 'coverDetail.driver.claimsDetails'
        });
    };

    const claimTooltip = <span>An 'at fault' claim typically refers to any insurance claim which cannot be recovered from another party, either partially or in full.</span>;

    // order by ascending
    let stagedClaimDetails = currentClaimDetails ? currentClaimDetails.toJSON().sort((a, b) => a.claimNumber - b.claimNumber) : [];
    // handle odd situation where no status has been set yet but there is incoming claim details
    if (claimDetailsStatus.current.size === 0 && stagedClaimDetails.length > 0) {
        stagedClaimDetails.forEach((cd) => { setClaimDetailStatus(cd.claimNumber, { isTouched: touched }); });
    }

    if (touched) {
        stagedClaimDetails.forEach((claimDetail) => setClaimDetailStatus(claimDetail.claimNumber, { isTouched: true }));
    }

    // build up any new or missing claim detail
    if (anyPreviousClaimsCount > stagedClaimDetails.length) {
        const lastClaimNumber = stagedClaimDetails.length === 0 ? 0 : stagedClaimDetails[stagedClaimDetails.length - 1].claimNumber;
        const diff = anyPreviousClaimsCount - stagedClaimDetails.length;

        stagedClaimDetails = [
            ...stagedClaimDetails,
            ...Array(diff).fill({}).map((_, index) => {
                const claimNumber = lastClaimNumber + index + 1;

                // newly added items should not be in a touched state regardless
                setClaimDetailStatus(claimNumber, { isTouched: false });

                // if it was a missing dormant claim
                const dormantClaim = claimDetailsStatus.current.get(claimNumber).dormantData;

                if (dormantClaim) {
                    return dormantClaim;
                }
                // empty object that will be pushed into array when anyPreviousClaimsCount increases
                return {
                    claimNumber,
                    atFault: '',
                    within3Years: '',
                    moreThan3AndUpTo5YearsAgo: ''
                };
            })
        ];

        listChange(stagedClaimDetails);
    }
    // we have dormant claims and shouldn't include them into view
    if (anyPreviousClaimsCount < stagedClaimDetails.length) {
        // keep track of any removed claims
        stagedClaimDetails
            .filter((s) => s.claimNumber > anyPreviousClaimsCount)
            .forEach((claimDetail) => setClaimDetailStatus(claimDetail.claimNumber, { dormant: claimDetail }));

        stagedClaimDetails = stagedClaimDetails.filter((s) => s.claimNumber <= anyPreviousClaimsCount);

        listChange(stagedClaimDetails);
    }
    // When answers are UNSURE or YES for legacy claim questions
    if (!anyPreviousClaimsCount) {
        listChange([]);
        return <></>;
    }

    /**
     * Handles the change of claim details for a specific index and field
     * @param {number} claimNumber - the claim detail's claimNumber
     * @param {string} field - The field being updated ('atFault' or 'timeframe')
     * @param {Event} e - The event triggering the change
     * @return {void}
     */
    const handleClaimDetailsChange = (claimNumber, field) => (e) => {
        const currClaimDetail = stagedClaimDetails.find((c) => c.claimNumber === claimNumber);

        let atFaultValue = currClaimDetail.atFault || '';
        let timeframeValue = currClaimDetail.within3Years || '';

        switch (field) {
            case 'atFault':
                atFaultValue = e.target.getAttribute('value');
                break;
            case 'timeframe':
                timeframeValue = e.target.getAttribute('value');
                break;
            default:
                break;
        }

        // Update the claim details
        const claimIndex = stagedClaimDetails.findIndex((c) => c.claimNumber === claimNumber);
        stagedClaimDetails[claimIndex] = {
            ...currClaimDetail,
            atFault: atFaultValue,
            within3Years: timeframeValue,
            moreThan3AndUpTo5YearsAgo: timeframeValue === 'YES' ? 'NO' : 'YES'
        };
        // Stage the change in the claim details
        stageChange(stagedClaimDetails);
    };

    return stagedClaimDetails.map((claimDetail) => (
        <div className={classNames(classes.claims_detail_main)} key={`claimdetails_${claimDetail.claimNumber}`}>
            <div id={`claimdetails-container_${claimDetail.claimNumber}`}>
                <h4 className={classNames(classes.title)}>
                    Claim
                    {' '}
                    {claimDetail.claimNumber}
                    {anyPreviousClaimsCount > 1 && claimDetail.claimNumber === 1 ? <span className={classNames(classes.subtitle)}>(most recent claim)</span> : ''}
                </h4>
                <p className={classNames(classes.claimLabel)}>
                    Was the regular driver at fault?
                    <Tooltip trackingLabel={`claimdetails_atfault_tooltip_${claimDetail.claimNumber}`} variant={'light'} body={claimTooltip} />
                </p>
                <div className={classNames(classes.container)}>
                    <div
                        className={classNames(classes.atfault_radio_group)}
                        id={`claimdetails-atfault-radio_group_${claimDetail.claimNumber}`}
                    >
                        <div className={classNames(classes.buttonContainer)}>
                            {badgeButtons.map((item) => {
                                return (
                                    <Badge
                                        id={`claimdetails_atfault_${item.innerLabel}_${claimDetail.claimNumber}`}
                                        key={`claimdetails_atfault_${item.innerLabel}_${claimDetail.claimNumber}`}
                                        data-id={`claimdetails_atfault_${item.innerLabel}_${claimDetail.claimNumber}`}
                                        optionProps={item}
                                        schema={{
                                            identifier: `atfault-radio_${claimDetail.claimNumber}`,
                                            stateKey: `vertical.quote.form.coverDetail.driver.claimsDetail[${claimDetail.claimNumber - 1}].atFault`,
                                            field: {
                                                type: 'badgegroup',
                                                metaType: 'radio',
                                                UIComponent: 'badge_primary_semi_rounded',
                                                labelVariant: 'inner_label_container_xsmall'
                                            }
                                        }}
                                        value={claimDetail.atFault ?? ''}
                                        onChange={handleClaimDetailsChange(claimDetail.claimNumber, 'atFault')}
                                        /*
                                        * This uses an undocumented feature of the form system
                                        * providing rules and touched we can trigger the validation state
                                        * that the schema uses.
                                        * follow the yellow brick road: {@link packages/ctm-components/components/form/badge/badge.js:332}
                                        */
                                        touched={claimDetailsStatus.current.get(claimDetail.claimNumber).touched}
                                        valid={claimDetail.atFault !== ''}
                                    />
                                );
                            })}
                        </div>
                        <ValidationMessage manualControl
                            manualVisible
                            manualErrorMessage={'Please select if the regular driver was at fault or not'}
                            rules={{ validation: (claimDetailsStatus.current.get(claimDetail.claimNumber).touched && claimDetail.atFault === ''), position: 'left' }} />
                    </div>
                </div>
                <p className={classNames(classes.claimLabel)}>When was the claim made?</p>
                <div className={classNames(classes.container)}>
                    <div
                        className={classNames(classes.timeframe_radio_group)}
                        id={`claimdetails-timeframe-radio_group_${claimDetail.claimNumber}`}
                    >
                        <div className={classes.buttonContainer}>
                            {timeframeButtons.map((item) => {
                                return (
                                    <Badge
                                        id={`claimdetails_timeframe_${item.innerLabel}_${claimDetail.claimNumber}`}
                                        key={`claimdetails_timeframe_${item.innerLabel}_${claimDetail.claimNumber}`}
                                        data-id={`claimdetails_timeframe_${item.innerLabel}_${claimDetail.claimNumber}`}
                                        optionProps={item}
                                        schema={{
                                            identifier: `timeframe-radio_${claimDetail.claimNumber}`,
                                            stateKey: `vertical.quote.form.coverDetail.driver.claimsDetail[${claimDetail.claimNumber - 1}].within3Years`,
                                            field: {
                                                type: 'badgegroup',
                                                metaType: 'radio',
                                                UIComponent: 'badge_primary_semi_rounded',
                                                labelVariant: 'inner_label_container_xsmall'
                                            }
                                        }}
                                        value={claimDetail.within3Years ?? ''}
                                        onChange={handleClaimDetailsChange(claimDetail.claimNumber, 'timeframe')}
                                        /*
                                        * This uses an undocumented feature of the form system
                                        * providing rules and touched we can trigger the validation state
                                        * that the schema uses.
                                        * follow the yellow brick road: {@link packages/ctm-components/components/form/badge/badge.js:332}
                                        */
                                        touched={claimDetailsStatus.current.get(claimDetail.claimNumber).touched}
                                        valid={claimDetail.within3Years !== ''}
                                    />
                                );
                            })}
                        </div>
                        <ValidationMessage manualControl
                            manualVisible
                            manualErrorMessage={'Please select a claim period'}
                            rules={{ validation: (claimDetailsStatus.current.get(claimDetail.claimNumber).touched && claimDetail.within3Years === ''), position: 'left' }} />
                    </div>
                </div>
            </div>
        </div>
    ));
};

const mapStateToProps = (state) => {
    // eslint-disable-next-line radix
    const anyPreviousClaimsCount = parseInt(state.getIn('vertical.quote.form.anyPreviousClaimsCount'.split('.')));
    const currentClaimDetails = state.getIn(['vertical', 'quote', 'form', 'coverDetail', 'driver', 'claimsDetails']);
    const currentYear = new Date().getFullYear();

    return { anyPreviousClaimsCount, currentYear, currentClaimDetails };
};

const ClaimDetail = connect(mapStateToProps)(withRules(_ClaimDetail));

export { _ClaimDetail, ClaimDetail };
